<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useTemplateRefsList } from '@vueuse/core';
import { PlusCircleIcon } from '@heroicons/vue/20/solid';

import { useCalcApi } from '@/helpers/useCalcApi';

import { defaultIncomingProperty, defaultOutgoingProperty, defaultSupportingProperty } from '@/helpers/builder';
import { LoanType, PropertyDetailOption } from '@/helpers/const';
import { useFeatureFlags } from '@/helpers/useFeatureFlags';
import { useUserQuoteProcess } from '@/helpers/useUserQuoteProcess';

import PropertyDetailCard from './modules/PropertyDetails/PropertyDetailCard.vue';
import ScenarioTypeTabs from './modules/ScenarioTypeTabs.vue';

import { UserQuoteProcessStep } from '@/types';
import type { IncomingProperty, OutgoingProperty, PropertyDetailEntry, SupportingProperty } from '@/types';
import type { SectionStates } from '@/types/SectionState';

import { productMatrixNeedsBorrowerTypeForProperties } from '@/helpers/productTypeMatrix';

const calc = useCalcApi();

const { loanApp, state, saveAndGoNext, send, goBack } = useUserQuoteProcess();
const { isCommercialQuoteFlowEnabled } = useFeatureFlags();

const loadExistingProperties = (): { item: PropertyDetailEntry }[] => {
  const items: { item: PropertyDetailEntry }[] = [];

  loanApp.value.incomingProperties.forEach((p) => {
    items.push({ item: { ...p, propertyOptions: PropertyDetailOption.PROPERTY_TO_PURCHASE } });
  });

  loanApp.value.outgoingProperties.forEach((p) => {
    items.push({ item: { ...p, propertyOptions: PropertyDetailOption.PROPERTY_TO_SELL } });
  });

  loanApp.value.supportingProperties.forEach((p) => {
    items.push({ item: { ...p, propertyOptions: PropertyDetailOption.SUPPORTING_PROPERTY } });
  });

  return items;
};

const existingProperties = loadExistingProperties();
const properties = ref<{ item: PropertyDetailEntry }[]>(existingProperties);
const propertyRefs = useTemplateRefsList<InstanceType<typeof PropertyDetailCard>>();
const propertyCounter = ref(existingProperties.length);
const isLoading = ref(false);
const validationError = ref('');

const isSingleSecurity = computed(() => loanApp.value.loanType === LoanType.SINGLE_SECURITY);
const isPurchaseOnly = computed(() => loanApp.value.loanType === LoanType.PURCHASE_ONLY);
const isBridging = computed(() => loanApp.value.loanType === LoanType.BRIDGING);

const hasIncomingProperty = computed(() =>
  properties.value.some((p) => p.item.propertyOptions === PropertyDetailOption.PROPERTY_TO_PURCHASE),
);

const hasOutgoingProperty = computed(() =>
  properties.value.some((p) => p.item.propertyOptions === PropertyDetailOption.PROPERTY_TO_SELL),
);

const sectionKeys = computed<Array<keyof SectionStates>>(() => {
  if (isBridging.value) return ['incomingProperties', 'outgoingProperties'];

  if (isSingleSecurity.value) return ['outgoingProperties'];

  return ['incomingProperties'];
});

const propertyDefaults = computed(() => {
  if (isBridging.value) {
    if (!hasIncomingProperty.value) {
      return { defaultPropertyOptions: PropertyDetailOption.PROPERTY_TO_PURCHASE, isPropertyOptionsHidden: true };
    }
    if (!hasOutgoingProperty.value) {
      return { defaultPropertyOptions: PropertyDetailOption.PROPERTY_TO_SELL, isPropertyOptionsHidden: true };
    }
  } else if (isPurchaseOnly.value) {
    if (!hasIncomingProperty.value) {
      return { defaultPropertyOptions: PropertyDetailOption.PROPERTY_TO_PURCHASE, isPropertyOptionsHidden: true };
    }
    return { defaultPropertyOptions: PropertyDetailOption.SUPPORTING_PROPERTY, isPropertyOptionsHidden: true };
  } else if (!hasOutgoingProperty.value) {
    // Single Security
    return { defaultPropertyOptions: PropertyDetailOption.PROPERTY_TO_SELL, isPropertyOptionsHidden: true };
  }

  return { defaultPropertyOptions: undefined, isPropertyOptionsHidden: false };
});

const propertyDescription = computed(() => {
  if (isPurchaseOnly.value) {
    return 'Include any properties your client is using as a support property.';
  }
  if (isCommercialQuoteFlowEnabled()) {
    return 'Include any properties your client is selling, or using as a support property.';
  }
  return isSingleSecurity.value
    ? 'Include any properties your client is selling, or using as a support property.'
    : 'Include any properties your client is buying, selling, or using as a support property.';
});

const generatePropertyId = () => `property-detail-${propertyCounter.value++}`;

const validateItems = async () => {
  validationError.value = '';
  const results = await Promise.all(propertyRefs.value.map((propertyRef) => propertyRef.update()));
  return results.every(Boolean);
};

const hasPropertyOption = (option: string) => properties.value.some((p) => p.item.propertyOptions === option);

/** Purchase-only must not include sell/outgoing properties (form or persisted state). */
const enforcePurchaseOnlyNoOutgoing = (): boolean => {
  if (!isPurchaseOnly.value) return true;
  if (hasPropertyOption(PropertyDetailOption.PROPERTY_TO_SELL) || (loanApp.value.outgoingProperties?.length ?? 0) > 0) {
    validationError.value = 'Purchase-only cannot have outgoing/sell properties.';
    return false;
  }
  return true;
};

const validatePropertyRequirements = () => {
  if (isBridging.value) {
    const hasIncoming = hasPropertyOption(PropertyDetailOption.PROPERTY_TO_PURCHASE);
    const hasOutgoingOrSupporting =
      hasPropertyOption(PropertyDetailOption.PROPERTY_TO_SELL) ||
      hasPropertyOption(PropertyDetailOption.SUPPORTING_PROPERTY);

    if (!hasIncoming || !hasOutgoingOrSupporting) {
      validationError.value = 'bridging_property_requirement';
      return false;
    }
    return true;
  }

  if (isPurchaseOnly.value) {
    if (!hasPropertyOption(PropertyDetailOption.PROPERTY_TO_PURCHASE)) {
      validationError.value = 'You must nominate a property you are purchasing.';
      return false;
    }
    if (!enforcePurchaseOnlyNoOutgoing()) return false;
    return true;
  }

  if (!isSingleSecurity.value && !hasIncomingProperty.value) {
    validationError.value = 'You must add a property to purchase before continuing.';
    return false;
  }

  if (!properties.value.some((p) => p.item.propertyOptions)) {
    validationError.value = 'You must nominate at least one property towards this application.';
    return false;
  }

  return true;
};

const restrictedPropertyIds = ref(new Set<string>());
const hasSingleSecurityRequired = computed(() => restrictedPropertyIds.value.size > 0);

const onRestrictedChange = (id: string, restricted: boolean) => {
  const updated = new Set(restrictedPropertyIds.value);
  if (restricted) {
    updated.add(id);
  } else {
    updated.delete(id);
  }
  restrictedPropertyIds.value = updated;
};

const addItem = async () => {
  if (await validateItems()) {
    properties.value.push({ item: { id: generatePropertyId() } });
  }
};

const removeItem = (id: string) => {
  properties.value = properties.value.filter((p) => p.item.id !== id);
  onRestrictedChange(id, false);
};

const onPropertySaved = () => {
  validationError.value = '';

  if (isBridging.value && properties.value.length === 1) {
    properties.value.push({ item: { id: generatePropertyId() } });
  }
};

const goToSingleSecurity = () => {
  send({
    type: 'UPDATE',
    loanApp: {
      ...loanApp.value,
      loanType: LoanType.SINGLE_SECURITY,
    },
  });
  send({ type: 'GOTO', step: UserQuoteProcessStep.LoanPurpose });
};

const getPropertyCount = (index: number) => {
  const propertyOption = properties.value[index]?.item.propertyOptions;
  if (!propertyOption) {
    return 1;
  }

  return properties.value.reduce(
    (count, property, currentIndex) =>
      currentIndex < index && property.item.propertyOptions === propertyOption ? count + 1 : count,
    1,
  );
};

const saveAndContinue = async () => {
  if (!(await validateItems())) return;
  if (!validatePropertyRequirements()) return;

  isLoading.value = true;

  const incomingProperties: IncomingProperty[] = properties.value
    .filter((p) => p.item.propertyOptions === PropertyDetailOption.PROPERTY_TO_PURCHASE)
    .map((p) => ({ ...defaultIncomingProperty(), ...p.item }));

  const outgoingProperties: OutgoingProperty[] = properties.value
    .filter((p) => p.item.propertyOptions === PropertyDetailOption.PROPERTY_TO_SELL)
    .map((p) => ({ ...defaultOutgoingProperty(), ...p.item }));

  const supportingProperties: SupportingProperty[] = properties.value
    .filter((p) => p.item.propertyOptions === PropertyDetailOption.SUPPORTING_PROPERTY)
    .map((p) => ({ ...defaultSupportingProperty(), ...p.item }));

  if (!enforcePurchaseOnlyNoOutgoing()) {
    isLoading.value = false;
    return;
  }

  if (loanApp.value.loanType === LoanType.SINGLE_SECURITY) {
    try {
      const payload = {
        ...loanApp.value,
        outgoingProperties,
      };
      const endDebt = (await calc.getEndDebt(payload)) ?? 0;
      send({ type: 'UPDATE', loanApp: { ...loanApp.value, endDebt } });
    } catch (error) {
      console.error('Failed to calculate end debt:', error);
      send({ type: 'UPDATE', loanApp: { ...loanApp.value, endDebt: 0 } });
    }
  } else if (loanApp.value.loanType === LoanType.PURCHASE_ONLY) {
    send({ type: 'UPDATE', loanApp: { ...loanApp.value, endDebt: 0 } });
  }

  // Product matrix: when classification does not depend on borrower type, resolve here (e.g. all-commercial).
  // Otherwise defer to borrower details so borrower groups feed `borrowerType` into the matrix (JS-6902).
  const deferMatrixForBorrowerType = productMatrixNeedsBorrowerTypeForProperties(
    incomingProperties,
    outgoingProperties,
    supportingProperties,
  );

  if (deferMatrixForBorrowerType) {
    send({
      type: 'UPDATE',
      loanApp: {
        ...loanApp.value,
        productType: undefined,
        assessmentType: undefined,
        applicationBorrowerType: undefined,
      },
    });
  } else {
    try {
      const result = await calc.getProductType(loanApp.value, {
        incomingProperties,
        outgoingProperties,
        supportingProperties,
      });
      if (result?.loanType) {
        send({
          type: 'UPDATE',
          loanApp: {
            ...loanApp.value,
            productType: result.loanType,
            assessmentType: result.assessmentType,
            applicationBorrowerType: undefined,
          },
        });
      }
    } catch (error) {
      console.error('Failed to determine product type:', error);
    }
  }

  saveAndGoNext({ incomingProperties, outgoingProperties, supportingProperties });
  isLoading.value = false;
};

watch(
  properties,
  (val) => {
    if (val.length === 0) {
      properties.value.push({ item: { id: generatePropertyId() } });
    }
  },
  { immediate: true },
);
</script>

<template>
  <div class="w-full">
    <div class="mb-8 pb-2">
      <h2 class="text-2xl font-medium">
        Tell us about the <BxHighlight>properties involved in this quote</BxHighlight>.
      </h2>
      <p class="mt-2">{{ propertyDescription }}</p>
    </div>
    <div class="section-panel md:p-6">
      <div class="mb-6">
        <ScenarioTypeTabs title="Property" :section-key="sectionKeys" base-test-id="property-details" />
      </div>
      <template v-for="(property, index) in properties" :key="property?.item?.id">
        <PropertyDetailCard
          v-bind="propertyDefaults"
          :ref="propertyRefs.set"
          v-model="property.item"
          :index="getPropertyCount(index)"
          :has-incoming-property="hasIncomingProperty"
          class="mb-4"
          is-multiple
          @remove="removeItem"
          @saved="onPropertySaved"
          @restricted-change="onRestrictedChange"
        />
      </template>

      <div class="mt-6 flex justify-end">
        <BxButton id="property-details-add" data-test-id="property-details-add" variant="tertiary" @click="addItem()">
          <PlusCircleIcon class="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
          Add property
        </BxButton>
      </div>
    </div>

    <BxErrorPanel v-if="validationError" class="mt-6">
      <template v-if="validationError === 'bridging_property_requirement'">
        You must have <strong>one property to purchase</strong> and at least one <strong>property to sell</strong> or
        use as a <strong>supporting property</strong>.
      </template>
      <template v-else>
        {{ validationError }}
      </template>
    </BxErrorPanel>

    <div class="mt-6">
      <BxButton
        v-if="hasSingleSecurityRequired"
        id="property-details-single-security"
        variant="primary"
        :loading="isLoading"
        data-test-id="property-details-single-security"
        @click="goToSingleSecurity()"
      >
        Single security
      </BxButton>
      <BxButton
        v-else
        id="property-details-continue"
        :loading="isLoading"
        data-test-id="property-details-continue"
        variant="primary"
        @click="saveAndContinue()"
      >
        Save and continue
      </BxButton>
      <BxButton
        v-if="state.nextEvents.includes('BACK')"
        data-test-id="property-details-back"
        variant="link"
        class="ml-4"
        @click="goBack()"
      >
        Back
      </BxButton>
    </div>
  </div>
</template>
